import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { TextField, Typography, Grow, TextareaAutosize } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import MaskedInput from 'react-text-mask';
import classNames from 'classnames';
import { isNumber, isEmpty } from 'lodash';
import InputAdornment from '@material-ui/core/InputAdornment';
import Box from '@material-ui/core/Box';
import ADGMImage from '../ADGMImage';
import ADGMSelect from '../ADGMSelect';
import styles from './styles';

function TextMaskCustom(props) {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={ref => {
        inputRef(ref ? ref.inputElement : null);
      }}
      placeholderChar={'\u2000'}
      // pipe={autoCorrectedDatePipe}
      // guide
      // keepCharPositions
    />
  );
}

TextMaskCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

function ADGMTextField(props) {
  const {
    classes,
    error,
    warning,
    mask,
    variant,
    fullWidth,
    maxLength,
    startAdornment,
    endAdornment,
    required,
    label,
    style,
    customClasses,
    success,
    isPreFilled,
    min,
    max,
    onChange,
    textArea,
    autoFocus,
    helperText,
    disabled,
    isArabic,
    isPhoneField,
    isArabicLabel,
    icon,
    showDropdownAsPrefix,
    dropDownOptions,
    dropDownValue,
    onPrefixChange,
    infoHint,
    ...otherProps
  } = props;
  const lang = localStorage.getItem('lang');

  const handleChange = e => {
    const { value } = e.target;

    if (min || max) {
      if (!isNumber(+value)) return;
      if (min > value || max < value) return;
    }

    if (typeof onChange === 'function') {
      onChange(e);
    }
  };

  const handleDropDownChange = (_, { value }) => {
    onPrefixChange(value);
  };

  const FieldComponent = useMemo(() => (textArea ? TextareaAutosize : TextField), [textArea]);

  return (
    <div className={classNames(classes.container, customClasses?.container)} style={style}>
      {label && (
        <Typography
          component='p'
          className={classNames(classes.label, customClasses?.label, isArabicLabel && classes.arabicLabel)}
        >
          {label} {required && <span className={classes.textADGMRed}>*</span>} {icon && icon}
        </Typography>
      )}
      <FieldComponent
        variant={variant}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        maxLength={maxLength}
        className={classNames(classes.field, customClasses?.field, {
          [classes.fieldTextArea]: textArea,
          [classes.errorField]: !textArea && error,
          [classes.errorFieldTextArea]: textArea && error,
          [classes.sucessField]: success,
          [classes.preFilledField]: isPreFilled,
          [classes.preFilledField]: isPreFilled,
          [classes.disabled]: disabled,
          [classes.arabicField]: isArabic,
          [classes.phoneField]: lang === 'ar' && isPhoneField,
          [classes.noAdornedStartPadding]: showDropdownAsPrefix,
        })}
        InputProps={{
          ...(mask && { inputComponent: TextMaskCustom, inputProps: { mask, ...otherProps } }),
          ...(startAdornment && { startAdornment: <InputAdornment position='start'>{startAdornment}</InputAdornment> }),
          ...(showDropdownAsPrefix === true && {
            startAdornment: (
              <ADGMSelect
                value={dropDownValue}
                options={dropDownOptions}
                onChange={handleDropDownChange}
                customClasses={{
                  container: customClasses.adornmentDropDownContainer || classes.adornmentDropDownContainer,
                  select: customClasses.adornmentDropDown || classes.adornmentDropDown,
                }}
                hideClearSelectionBtn
              />
            ),
          }),
          ...(endAdornment && { endAdornment: <InputAdornment position='start'>{endAdornment}</InputAdornment> }),
          ...(success && { endAdornment: <ADGMImage name='sucessTick.svg' alt='success' /> }),
          ...(error && { endAdornment: <ErrorIcon color='error' /> }),
          ...(disabled === true && { disabled: true }),
          classes: { input: customClasses?.input },
        }}
        onChange={handleChange}
        onWheel={e => e.target?.blur()}
        {...otherProps}
      />
      {infoHint && <Box className={classes.hintText}>{infoHint}</Box>}

      {!isEmpty(helperText) && (
        <Grow in={!isEmpty(helperText)} timeout={400}>
          <span className={classNames(classes.helperMsg, customClasses?.helperMsg)}>{helperText}</span>
        </Grow>
      )}
      {!isEmpty(error) && (
        <Grow in={!isEmpty(error)} timeout={400}>
          <span className={classNames(classes.errorMsg, customClasses?.errorMsg)}>{error}</span>
        </Grow>
      )}
      {!isEmpty(warning) && (
        <Grow in={!isEmpty(warning)} timeout={400}>
          <span className={customClasses?.warningMsg}>{warning}</span>
        </Grow>
      )}
    </div>
  );
}

ADGMTextField.propTypes = {
  classes: PropTypes.object.isRequired,
  style: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  mask: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  required: PropTypes.bool,
  success: PropTypes.string,
  warning: PropTypes.string,
  error: PropTypes.string,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  maxLength: PropTypes.number,
  startAdornment: PropTypes.object,
  endAdornment: PropTypes.object,
  isPreFilled: PropTypes.bool,
  textArea: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  customClasses: PropTypes.object,
  autoFocus: PropTypes.bool,
  helperText: PropTypes.string,
  isArabic: PropTypes.bool,
  isArabicLabel: PropTypes.bool,
  icon: PropTypes.node,
  showDropdownAsPrefix: PropTypes.bool,
  dropDownOptions: PropTypes.array,
  dropDownValue: PropTypes.any,
  infoHint: PropTypes.string,
  onPrefixChange: PropTypes.func,
  isPhoneField: PropTypes.bool,
};

ADGMTextField.defaultProps = {
  style: {},
  label: null,
  mask: null,
  required: false,
  success: null,
  warning: null,
  error: null,
  variant: 'outlined',
  fullWidth: true,
  maxLength: null,
  startAdornment: null,
  endAdornment: null,
  isPreFilled: false,
  textArea: false,
  min: null,
  max: null,
  disabled: false,
  onChange: null,
  customClasses: null,
  autoFocus: false,
  isArabic: false,
  isPhoneField: false,
  isArabicLabel: false,
  helperText: '',
  icon: null,
  showDropdownAsPrefix: false,
  dropDownOptions: [],
  dropDownValue: null,
  infoHint: '',
  onPrefixChange: () => {},
};

export default withStyles(styles)(ADGMTextField);
